import { push } from 'react-router-redux';
import api from '../../api/index.js';
import organization from './actions.js';
import app from '../app/actions.js';
import ui from '../ui/actions.js';
import { searchByParams, searchByID } from '../app/operations.js';
import { orgToState, logoUrl, listOrg } from './transform.js';
import { toSubmissionError, toSubmissionErrorSytemPortal } from '~/utils/transform.js';

import { isUUID } from '~/utils/index.js';
import mapping from '~/utils/mapping.json';


// =============================================================================
// GET
// =============================================================================
/*
 * Search for organizations
 * redux-form onSubmit function, called by handleSubmit
 */
export async function search(values, dispatch) {

    const {
        terms, portals, archived, deleted,
    } = values.toJS();

    if (isUUID(terms)) {
        searchByID('organizations', terms, dispatch);

    } else {
        const params = {
            types: mapping.vertex_types.by_name.ORGANIZATION,
            limit: -1,
            terms,
            portals,
            archived,
            deleted,
        };

        searchByParams(params, dispatch);
    }
}

// Get organization
export const fetchOrganization = id => async (dispatch, getState) => {

    try {
        const { data } = await dispatch(api.get(`/organizations/${id}`));

        dispatch(organization.data.set(orgToState(data, getState)));

        if (data.portalAffiliates) {
            dispatch(app.affiliateSelector.confirmedItems.set(data.portalAffiliates));
        }

    } catch (error) {
        // 404 Not Found
        if (error.response && error.response.status === 404) {
            dispatch(ui.flash.error({ header: 'Organization not found' }));
        }
        throw error;
    }
};

// =============================================================================
// PATCH
// =============================================================================
/*
 * Update an organization
 * redux-form onSubmit function, called by handleSubmit
 */
export async function patchOrganization(values, dispatch, props) {
    const {
        location: { pathname },
        match: { params: { id } },
        initialValues,
    } = props;
    const { licenses: initialLicenses } = initialValues.toJS();
    const { licenses, ...data } = values.toJS();
    const { portal } = data;
    if (data.enable_private_collaboratory_feature !== undefined && !data.enable_private_collaboratory_feature) {
        data.private_collaboratory = false;
    }
    try {
        await dispatch(api.patch(`/organizations/${id}`, { data }));

        if (portal) {
            await handleLicenses(id, licenses, initialLicenses)(dispatch);
        }

        dispatch(push(pathname.replace(/\/edit$/, '')));
        dispatch(ui.flash.success({ header: 'Organization updated successfully' }));

    } catch (error) {
        throw toSubmissionError(error);

    }
}

// =============================================================================
// POST
// =============================================================================
/*
 * Create a new organization
 * redux-form onSubmit function, called by handleSubmit
 */
export async function postOrganization(values, dispatch, props) {
    const {
        location: { pathname },
    } = props;
    const { licenses, ...data } = values.toJS();
    const { portal } = data;
    const state = dispatch((_, getState) => getState());

    const author = state.get('author');
    const authorId = author ? author.get('author_id') : null;


        if (authorId) {
            // Add author_id to the data payload
            data.author_id = authorId;
        } else {
            data.author_id = "53805e9c-66ea-48ac-4c69-3077d60a32e7"
            console.warn('author_id is undefined or null');
        }

    try {
        console.log("organization operations data", data)
        const { data: { id } } = await dispatch(api.post('/organizations', { data }));

        if (portal) {
            await Promise.all(licenses.map(productID => putLicense(id, productID, dispatch)));
        }

        dispatch(push(pathname.replace(/create$/, id)));
        dispatch(ui.flash.success({ header: 'Organization created successfully' }));

    } catch (error) {
        throw toSubmissionError(error);
    }
}

// =============================================================================
// License
// =============================================================================
// export for testing
export function handleLicenses(orgID, values, initials = []) {
    return async (dispatch) => {
        const deleteItems = initials.filter(i => !values.includes(i));
        const addItems = values.filter(i => !initials.includes(i));

        if (deleteItems.length) {
            await Promise.all(deleteItems.map(productID => deleteLicense(orgID, productID, dispatch)));
        }

        if (addItems.length) {
            return Promise.all(addItems.map(productID => putLicense(orgID, productID, dispatch)));
        }
    };
}

async function putLicense(orgID, productID, dispatch) {
    try {
        await dispatch(api.put(`/organizations/${orgID}/licenses/${productID}`));
    } catch (error) {
        throw error;
    }
}

async function deleteLicense(orgID, productID, dispatch) {
    try {
        await dispatch(api.delete(`/organizations/${orgID}/licenses/${productID}`));
    } catch (error) {
        throw error;
    }
}

// Get super portal
export const fetchSuperPortal = id => async (dispatch, getState) => {

    try {
        const { data } = await dispatch(api.get(`/superportals/${id}`));

        if (data.portal_affiliates !== undefined) {
            const confirmedAffiliates = logoUrl(data.portal_affiliates);
            dispatch(app.affiliateSelector.confirmedItems.set(confirmedAffiliates));
        }

        try {
            const { data } = await dispatch(api.get(`/organizations/${id}`));
            data.superPortal = true;
            dispatch(organization.data.set(orgToState(data, getState)));

        } catch (error) {
            // 404 Not Found
            if (error.response && error.response.status === 404) {
                dispatch(ui.flash.error({ header: 'System portal not found' }));
            }
            throw error;
        }

    } catch (error) {
        // 404 Not Found
        if (error.response && error.response.status === 404) {
            dispatch(ui.flash.error({ header: 'System portal not found' }));
        }
        throw error;
    }
};

// =============================================================================
// POST
// =============================================================================
/*
 * Create a new super portal
 * redux-form onSubmit function, called by handleSubmit
 */
export async function postSuperPortal(values, dispatch, props) {
    const {
        location: { pathname },
    } = props;
    const { licenses, ...data } = values.toJS();
    const { portal } = data;

    try {
        const { data: { id } } = await dispatch(api.post('/superportals', { data }));

        if (portal) {
            await Promise.all(licenses.map(productID => putLicense(id, productID, dispatch)));
        }

        dispatch(push(pathname.replace(/create$/, id)));
        dispatch(ui.flash.success({ header: 'System portal created successfully' }));

    } catch (error) {
        throw toSubmissionErrorSytemPortal(error);
    }
}

// =============================================================================
// PATCH
// =============================================================================
/*
 * Update an super portal
 * redux-form onSubmit function, called by handleSubmit
 */
export async function patchSuperPortal(values, dispatch, props) {
    const {
        location: { pathname },
        match: { params: { id } },
        initialValues,
    } = props;
    const { licenses: initialLicenses } = initialValues.toJS();
    const { licenses, ...data } = values.toJS();
    const { portal } = data;

    try {
        await dispatch(api.patch(`/superportals/${id}`, { data }));

        if (portal) {
            await handleLicenses(id, licenses, initialLicenses)(dispatch);
        }

        dispatch(push(pathname.replace(/\/edit$/, '')));
        dispatch(ui.flash.success({ header: 'System portal updated successfully' }));

    } catch (error) {
        throw toSubmissionErrorSytemPortal(error);

    }
}

// =============================================================================
// GET
// =============================================================================
/*
 * Search for portals for super portals
 * redux-form onSubmit function, called by handleSubmit
 */
export async function searchPortalsSuperPortal(values, dispatch) {

    const {
        terms, archived, deleted,
    } = values.toJS();

    if (isUUID(terms)) {
        searchByID('organizations', terms, dispatch);

    } else {
        const params = {
            types: mapping.vertex_types.by_name.ORGANIZATION,
            limit: -1,
            terms,
            superportal_affiliates: true,
            archived,
            deleted,
        };

        searchByParams(params, dispatch);
    }
}

// =============================================================================
// GET
// =============================================================================
/*
 * List for portals for super portals
 * redux-form onSubmit function, called by handleSubmit
 */


export async function listPortalsSuperPortal(values, dispatch) {

    const {
        terms,
    } = values.toJS();

    try {
        const { data } = await dispatch(api.get('/superportals'));

        const filterData = data.filter(ele => ele.name.toLowerCase().includes(terms.toLowerCase()) === true);

        dispatch(app.search.results.set(listOrg(filterData)));

    } catch (error) {
        // 404 Not Found
        if (error.response && error.response.status === 404) {
            dispatch(ui.flash.error({ header: 'Organization not found' }));
        }
        throw error;
    }
}

// =============================================================================
// GET
// =============================================================================
/*
 * Get super portals list
 * redux-form onSubmit function, called by handleSubmit
 */
export const getSuperPortalList = () => async (dispatch) => {
    try {
        const { data } = await dispatch(api.get('/superportals'));

        dispatch(app.search.results.set(listOrg(data)));

    } catch (error) {
        // 404 Not Found
        if (error.response && error.response.status === 404) {
            dispatch(ui.flash.error({ header: 'Organization not found' }));
        }
        throw error;
    }
};

export const clearSearchPortalList = () => async (dispatch) => {
    try {
        const { data } = await dispatch(api.get('/superportals'));

        dispatch(app.search.results.set(listOrg(data)));

    } catch (error) {
        // 404 Not Found
        if (error.response && error.response.status === 404) {
            dispatch(ui.flash.error({ header: 'Organization not found' }));
        }
        throw error;
    }
};
